import { useState, useEffect, cloneElement } from "react";
import { ProSidebar, Menu, MenuItem } from "react-pro-sidebar";
import { Box, IconButton, Typography, useTheme,  useMediaQuery } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import "react-pro-sidebar/dist/css/styles.css";
import { tokens } from "../../theme";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import priceService from "../../services/PriceService";
import authService from "../../services/AuthService";
import splinterlandsService from "../../services/SplinterlandsService";
import DiamondTwoToneIcon from "@mui/icons-material/DiamondTwoTone";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import MarketSearch from "./MarketSearch";
import CashbackDisplayCompact from "../../components/market/CashbackDisplayCompact";
import LandFilters from "../../components/filters/LandFilters";
import ItemFilters from "../../components/filters/ItemFilters";



const Item = ({ title, to, icon, selected, setSelected, showFire = false, isMobile, onMobileItemClick }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const accent = '#6c3eb6';

  const iconColor = selected === title ? accent : colors.grey[400];
  const iconSized = icon ? cloneElement(icon, { sx: { ...(icon.props?.sx || {}), color: iconColor, fontSize: 22 } }) : null;
  
  // Erweiterte Klickbehandlung für mobile Geräte 
  const handleClick = () => {
    setSelected(title);
    // Bei mobilen Geräten zusätzlich die Sidebar schließen
    if (isMobile && onMobileItemClick) {
      onMobileItemClick();
    }
  };

  return (
    <MenuItem
      active={selected === title}
      style={{
        color: selected === title ? colors.grey[100] : colors.grey[300],
      }}
      onClick={handleClick}
      icon={iconSized}
    >
      <Box display="flex" alignItems="center">
        <Typography>{title}</Typography>
        {showFire && (
          <Box ml={1} display="flex" alignItems="center">
            <LocalFireDepartmentIcon
              sx={{
                color: theme.palette.mode === 'dark' ? '#ff7043' : '#f44336',
                animation: 'flicker 1.5s infinite'
              }}
            />
          </Box>
        )}
      </Box>
      <Link to={to} />
    </MenuItem>
  );
};

// Custom icon component that uses MUI icons for a consistent look
const Sidebar = ({ isSidebar, setIsSidebar, isMobile }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [selected, setSelected] = useState("Dashboard");
  const [, setIsFlywheelActive] = useState(false);
  const location = useLocation();
  
  // Check if we're on the market page (needed for initial collapsed state)
  const isMarketPage = location.pathname === '/market' || location.pathname === '/market/land' || location.pathname === '/market/items' || location.pathname === '/market/mycards';
  
  // Initialize collapsed state: collapsed only on mobile devices
  const [collapsed, setCollapsed] = useState(() => {
    if (isMobile) {
      // On mobile: collapse unless we're on market page with sidebar open
      if (isMarketPage && isSidebar) {
        return false; // Start uncollapsed when opening filters on mobile market
      }
      return true; // Otherwise collapse on mobile
    }
    // On desktop: start uncollapsed (user can manually collapse if desired)
    return false;
  });
  
  // User data
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [, setPlayerData] = useState(null);
  const [, setBalances] = useState({
    sps: 0,
    dec: 0,
    voucher: 0,
    credits: 0
  });

  // Land filter data for sidebar
  const [landFilterData, setLandFilterData] = useState({
    availableRegions: [],
    availableTerritories: [],
    availableRarities: [],
    availableResources: [],
    availablePlotStatuses: [],
    availableLandTypes: [],
    availableWorksites: []
  });

  // Set initial selected item based on path
  useEffect(() => {
    if (location.pathname === '/') setSelected("SPS");
    else if (location.pathname === '/dec') setSelected("DEC");
    else if (location.pathname === '/market' || location.pathname === '/market/land') setSelected("Market");
  }, [location.pathname]);

  // Update collapsed state when route changes (mobile only)
  useEffect(() => {
    if (isMobile && !location.pathname.startsWith('/market')) {
      // On mobile non-market pages, ensure sidebar is collapsed
      setCollapsed(true);
    }
  }, [location.pathname, isMobile]);

  // Subscribe to auth status
  useEffect(() => {
    const unsubscribe = authService.subscribe((data) => {
      setIsLoggedIn(data.isLoggedIn);
      setCurrentUser(data.currentUser);
    });
    
    return () => unsubscribe();
  }, []);
  
  // Subscribe to Splinterlands data
  useEffect(() => {
    const unsubscribe = splinterlandsService.subscribe((data) => {
      setPlayerData(data.playerData);
      setBalances(data.balances);
    });
    
    return () => unsubscribe();
  }, []);

  // Listen for land filter data updates
  useEffect(() => {
    const handleLandFilterDataUpdate = (event) => {
      setLandFilterData(event.detail);
    };

    window.addEventListener('landFilterDataUpdate', handleLandFilterDataUpdate);
    
    return () => {
      window.removeEventListener('landFilterDataUpdate', handleLandFilterDataUpdate);
    };
  }, []);

  useEffect(() => {
    // React to price changes and update flywheel status
    const unsubscribe = priceService.subscribe((data) => {
      setIsFlywheelActive(data.isFlywheelActive);
    });

    return () => {
      unsubscribe(); // Cleanup
    };
  }, []);
  
  // Number formatting
  const formatNumber = (value) => {
    if (!value && value !== 0) return '0';
    return value.toLocaleString('en-US', { maximumFractionDigits: 3 });
  };
  
  // Create avatar letter
  const getAvatarLetter = (username) => {
    return username ? username.charAt(0).toUpperCase() : 'B';
  };

  // Reagieren auf Änderungen im Mobile-Status und Sidebar-Status
  useEffect(() => {
    if (isMobile && isMarketPage) {
      // On market pages, sync collapsed with sidebar visibility
      setCollapsed(!isSidebar);
    } else if (isMobile) {
      // On non-market pages, always collapse on mobile
      setCollapsed(true);
    }
    // On desktop, let user control collapsed state manually (don't force it)
  }, [isMobile, isMarketPage, isSidebar]);
  
  // Funktion zum Schließen der Sidebar nach Navigation auf mobilen Geräten
  const handleMobileItemClick = () => {
    if (isMobile) {
      // Sidebar auf mobilen Geräten schließen
      setIsSidebar(false);
    }
  };

  return (
    <>
      {/* Overlay zum Schließen der Sidebar bei Klick außerhalb (nur auf Mobilgeräten) */}
      {isMobile && isSidebar && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1100
          }}
          onClick={() => setIsSidebar(false)}
        />
      )}
      
      <Box
        sx={{
          position: isMobile ? 'fixed' : 'sticky',
          top: isMobile ? 0 : 0,
          zIndex: 1200,
          height: '100vh',
          width: isMobile ? '100vw' : 'auto',
          overflow: 'hidden',
          flexShrink: 0,
          "& .pro-sidebar-inner": {
            background: `${colors.primary[400]} !important`,
            height: '100% !important',
            overflow: 'auto !important',
          },
          "& .pro-icon-wrapper": {
            backgroundColor: "transparent !important",
          },
          "& .pro-inner-item": {
            padding: "8px 28px 8px 20px !important",
            color: `${colors.grey[300]} !important`,
          },
          "& .pro-inner-item:hover": {
            backgroundColor: `rgba(255,255,255,0.04) !important`,
            color: `${colors.grey[100]} !important`,
          },
          "& .pro-menu-item": {
            marginBottom: '4px',
          },
          "& .pro-menu-item.active .pro-inner-item": {
            color: `${colors.grey[100]} !important`,
            backgroundColor: `rgba(108, 62, 182, 0.12) !important`,
            borderLeft: '3px solid #6c3eb6',
          },
          "& .pro-menu-item.active .pro-icon-wrapper": {
            color: `#6c3eb6 !important`,
          },
          "& .pro-inner-item > svg": {
            fontSize: '22px',
          },
        "@keyframes flicker": {
          "0%": { opacity: 0.7 },
          "50%": { opacity: 1 },
          "100%": { opacity: 0.7 }
        },
        "@keyframes pulse": {
          "0%": { boxShadow: "0 0 0 0 rgba(244, 67, 54, 0.7)" },
          "70%": { boxShadow: "0 0 0 10px rgba(244, 67, 54, 0)" },
          "100%": { boxShadow: "0 0 0 0 rgba(244, 67, 54, 0)" }
        },
        // Sidebar für mobile Ansicht verstecken, wenn nicht aktiv
        transform: isMobile && !isSidebar ? 'translateX(-100%)' : 'translateX(0)',
        transition: 'transform 0.3s ease',
      }}
    >
      <ProSidebar collapsed={collapsed} width={isMobile ? "100%" : "300px"}>
        <Menu iconShape="square">
          {/* LOGO AND MENU ICON */}
          <MenuItem
            style={{
              margin: "10px 0 20px 0",
              color: colors.grey[100],
              cursor: 'default'
            }}
            icon={collapsed ? (
              <Box 
                display="flex" 
                alignItems="center" 
                justifyContent="space-between"
                sx={{ width: '100%' }}
              >
                <IconButton
                  component={Link}
                  to="/"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelected("SPS");
                    if (isMobile) {
                      setIsSidebar(false);
                    }
                  }}
                  sx={{
                    color: '#6c3eb6',
                    padding: '4px',
                    '&:hover': {
                      backgroundColor: 'rgba(108, 62, 182, 0.1)',
                      opacity: 0.8
                    }
                  }}
                  size="small"
                >
                  <DiamondTwoToneIcon />
                </IconButton>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsSidebar(!isSidebar);
                    if (!isMobile) {
                      setCollapsed(!collapsed);
                    }
                  }}
                  sx={{
                    color: colors.grey[100],
                    padding: '4px',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                  size="small"
                >
                  <ChevronRightIcon />
                </IconButton>
              </Box>
            ) : undefined}
          >
            {!collapsed && (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                ml="15px"
              >
                <IconButton
                  component={Link}
                  to="/"
                  onClick={() => {
                    setSelected("SPS");
                    if (isMobile) {
                      setIsSidebar(false);
                    }
                  }}
                  sx={{
                    color: 'inherit',
                    padding: '8px',
                    '&:hover': {
                      backgroundColor: 'rgba(108, 62, 182, 0.1)',
                      opacity: 0.8
                    }
                  }}
                >
                  <Box display="flex" alignItems="center">
                    <Typography
                      variant="h6"
                      sx={{
                        fontFamily: '"Press Start 2P", monospace',
                        fontSize: '14px',
                        letterSpacing: '1px',
                        color: '#f7a52d',
                        textTransform: 'lowercase',
                        lineHeight: 1.2,
                        textShadow: '0 1px 0 #1a1a1a'
                      }}
                    >
                      elmo-burns
                    </Typography>
                  </Box>
                </IconButton>
                <IconButton 
                  onClick={() => {
                    setIsSidebar(!isSidebar);
                    if (!isMobile) {
                      setCollapsed(!collapsed);
                    }
                  }}
                  sx={{
                    color: colors.grey[100],
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  <ChevronLeftIcon />
                </IconButton>
              </Box>
            )}
          </MenuItem>

          <Box
            paddingLeft={collapsed ? undefined : "10%"}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: isMobile ? 1 : 1.5,
              height: '100%',
            }}
          >

            {/* Navigation Items - hide in market view for better filter accessibility */}
            {!isMarketPage && (
              <>
                <Item
                  title="SPS"
                  to="/"
                  icon={<DiamondTwoToneIcon />}
                  selected={selected}
                  setSelected={setSelected}
                  isMobile={isMobile}
                  onMobileItemClick={handleMobileItemClick}
                />

                <Item
                  title="DEC"
                  to="/dec"
                  icon={<DiamondTwoToneIcon />}
                  selected={selected}
                  setSelected={setSelected}
                  isMobile={isMobile}
                  onMobileItemClick={handleMobileItemClick}
                />

                <Item
                  title="Market"
                  to="/market"
                  icon={<ShoppingCartIcon />}
                  selected={selected}
                  setSelected={setSelected}
                  isMobile={isMobile}
                  onMobileItemClick={handleMobileItemClick}
                />

                <Item
                  title="Card Winrates"
                  to="/card-winrates"
                  icon={<LeaderboardIcon />}
                  selected={selected}
                  setSelected={setSelected}
                  isMobile={isMobile}
                  onMobileItemClick={handleMobileItemClick}
                />
              </>
            )}
            
            {/* Market Search - only show when on cards market page or mycards page and sidebar is accessible */}
            {(!collapsed || (isMobile && isSidebar)) && isMarketPage && (location.pathname === '/market' || location.pathname === '/market/mycards') && (
              <MarketSearch />
            )}
            
            {/* Land Filters - only show when on land market page and sidebar is accessible */}
            {(!collapsed || (isMobile && isSidebar)) && isMarketPage && location.pathname === '/market/land' && (
              <Box sx={{ 
                maxWidth: isMobile ? 'calc(100vw - 40px)' : '100%',
                overflow: 'hidden'
              }}>
                <LandFilters 
                  onFiltersChange={(filters) => {
                    // Send filter data to parent component via custom event
                    window.dispatchEvent(new CustomEvent('landFiltersChanged', { detail: filters }));
                  }}
                  availableRegions={landFilterData.availableRegions}
                  availableTerritories={landFilterData.availableTerritories}
                  availableRarities={landFilterData.availableRarities}
                  availableResources={landFilterData.availableResources}
                  availablePlotStatuses={landFilterData.availablePlotStatuses}
                  availableLandTypes={landFilterData.availableLandTypes}
                  availableWorksites={landFilterData.availableWorksites}
                  isMobile={isMobile}
                />
              </Box>
            )}
            
            {/* Item Filters - only show when on items market page and sidebar is accessible */}
            {(!collapsed || (isMobile && isSidebar)) && isMarketPage && location.pathname === '/market/items' && (
              <Box sx={{ 
                maxWidth: isMobile ? 'calc(100vw - 40px)' : '100%',
                overflow: 'hidden'
              }}>
                <ItemFilters 
                  onFiltersChange={(filters) => {
                    // Send filter data to parent component via custom event
                    window.dispatchEvent(new CustomEvent('itemFiltersChanged', { detail: filters }));
                  }}
                  isMobile={isMobile}
                />
              </Box>
            )}

            {/* Cashback Display - pinned at bottom on market pages */}
            {(!collapsed || (isMobile && isSidebar)) && isMarketPage && isLoggedIn && currentUser && currentUser.username && (
              <Box sx={{ mt: 'auto', pb: isMobile ? 2 : 3 }}>
                <CashbackDisplayCompact playerName={currentUser.username} />
              </Box>
            )}
            
          </Box>
        </Menu>
      </ProSidebar>
    </Box>
    </>
  );
};

export default Sidebar;
